"use client";

import { motion } from "framer-motion";
import { AlertTriangle, Ghost, KeyRound } from "lucide-react";

const painPoints = [
  {
    icon: <AlertTriangle className="w-6 h-6" />,
    title: "Install roulette",
    description:
      "Broken migrations leaving dual services running, auth broken, and the dashboard dead. You end up manually killing old services and cleaning config by hand.",
    glowColor: "coral-bright",
  },
  {
    icon: <Ghost className="w-6 h-6" />,
    title: "It runs, but doesn't answer",
    description:
      "Gateways apparently up, dashboards opening, but bots silently ignoring every message. Misconfigured memory or auth profiles causing 'nothing happens' behavior.",
    glowColor: "cyan-bright",
  },
  {
    icon: <KeyRound className="w-6 h-6" />,
    title: "Channels & tokens everywhere",
    description:
      "Per-channel bot tokens, Discord intents, WhatsApp linking flows, and Clawbiz config that must all line up. One mis-typed ID or stale token stalls the whole system.",
    glowColor: "coral-mid",
  },
];

export const WhySection = () => {
  return (
    <section className="relative w-full py-20 md:py-32 overflow-hidden">

      <div className="section-container relative z-10 space-y-12">
        <motion.div
          className="text-center space-y-4 max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-medium leading-tight">
            Setting this up shouldn't be a <span className="text-primary">weekend project</span>.
          </h2>
          <p className="text-lg text-muted-foreground">
            We've read the threads. We fix this end-to-end.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {painPoints.map((point, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative group"
            >
              {/* Hover glow */}
              <div className={`absolute -inset-1 rounded-2xl bg-[hsl(var(--${point.glowColor})/0.08)] opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500`} />
              
              <div className="relative glass-card rounded-2xl p-6 space-y-4 hover:bg-white/[0.04] transition-all duration-300">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(var(--coral-bright)/0.15)] to-[hsl(var(--cyan-bright)/0.1)] flex items-center justify-center">
                  <div className="text-primary">{point.icon}</div>
                </div>
                <h3 className="text-xl font-display font-medium">{point.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{point.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
